import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import BannerEditor from "@/pages/BannerEditor";
import NumberAssets from "@/pages/NumberAssets";
import LotteryResultPage from "@/pages/LotteryResultPage";
import PublicBanner from "@/pages/PublicBanner";
import Lotteries from "@/pages/Lotteries";
import Analytics from "@/pages/Analytics";
import Profile from "@/pages/Profile";
import CustomFonts from "@/pages/CustomFonts";
import Trash from "@/pages/Trash";
import Team from "@/pages/Team";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ResetPassword from "@/pages/ResetPassword";
import Users from "@/pages/Users";
import Brands from "@/pages/Brands";
import Recipients from "@/pages/Recipients";
import EmailQueue from "@/pages/EmailQueue";
import { Toaster } from "@/components/ui/sonner";
import { UserProvider } from "@/contexts/UserContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Set a reasonable timeout for all requests
axios.defaults.timeout = 15000;
// Send cookies with every request
axios.defaults.withCredentials = true;

export const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    sessionStorage.setItem('token', token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    sessionStorage.removeItem('token');
  }
};

// Global response interceptor — retry once on 401 before logging out
let isRedirectingToLogin = false;
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !isRedirectingToLogin &&
      !originalRequest._retried &&
      !originalRequest.url?.includes("/auth/")  &&
      !originalRequest.url?.includes("/contact/")
    ) {
      originalRequest._retried = true;
      try {
        return await axios(originalRequest);
      } catch (retryError) {
        if (retryError.response?.status === 401) {
          isRedirectingToLogin = true;
          setAuthToken(null);
          window.location.href = "/login";
          setTimeout(() => { isRedirectingToLogin = false; }, 2000);
        }
        return Promise.reject(retryError);
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore Bearer token from session (httpOnly cookie is primary auth)
    const token = sessionStorage.getItem('token');
    if (token) {
      setAuthToken(token);
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <UserProvider>
          <Routes>
            {/* Public landing page */}
            <Route path="/welcome" element={<LandingPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/banner/:bannerId" element={<PublicBanner />} />
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <Navigate to="/app" replace />
                ) : (
                  <Login setIsAuthenticated={setIsAuthenticated} />
                )
              }
            />
            <Route
              path="/app"
              element={
                isAuthenticated ? (
                  <Dashboard />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <Navigate to="/app" replace />
                ) : (
                  <Navigate to="/welcome" replace />
                )
              }
            />
            <Route
              path="/banner-editor/:bannerId?"
              element={
                isAuthenticated ? (
                  <BannerEditor />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/number-assets"
              element={
                isAuthenticated ? (
                  <NumberAssets />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/lottery-result"
              element={
                isAuthenticated ? (
                  <LotteryResultPage />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/lotteries"
              element={
                isAuthenticated ? (
                  <Lotteries />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/analytics"
              element={
                isAuthenticated ? (
                  <Analytics />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/profile"
              element={
                isAuthenticated ? (
                  <Profile />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/custom-fonts"
              element={
                isAuthenticated ? (
                  <CustomFonts />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/trash"
              element={
                isAuthenticated ? (
                  <Trash />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/team"
              element={
                isAuthenticated ? (
                  <Team />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/users"
              element={
                isAuthenticated ? (
                  <Users />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/brands"
              element={
                isAuthenticated ? (
                  <Brands />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/recipients"
              element={
                isAuthenticated ? (
                  <Recipients />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/email-queue"
              element={
                isAuthenticated ? (
                  <EmailQueue />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </UserProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;